<script lang="ts">
    import {
        VpArrowLeftIcon,
        VpArrowRightIcon,
        VpCloseIcon,
        VpLightbulbIcon,
        VpWarningErrorIcon,
    } from '@decathlon-experience/icons/svelte';
    import * as Sentry from '@sentry/browser';
    import { onDestroy, onMount } from 'svelte';
    import { writable } from 'svelte/store';

    import { trans } from '$assets/translator.js';
    import {
        imageOptimize,
        uploadImageToMedia,
    } from '$backoffice/views/MediaManager/utils';
    import cn from '$common/utils/cn';
    import Modal from '$saga/components/Modal.svelte';

    import FocalPointEditor from './FocalPointEditor.svelte';
    import ImageCropEditor from './ImageCropEditor.svelte';
    import MultiMediaList from './MultiMediaList.svelte';
    import MultiMediaUpload from './MultiMediaUpload.svelte';
    import type { ImageItem } from './types';

    export let inputName: string;
    export let initialImageItems: ImageItem[];
    export let required: boolean = false;
    export let apiEndpoint: string;
    export let mediaBaseUrl: string;
    export let minImages: number = 0;
    export let maxImages: number | null = null;
    export let showCoverLabel: boolean = false;
    export let cropProxyUrl: string;

    let imageItems = initialImageItems;
    let blobUrlsToRevoke: string[] = [];
    let focalPointEditorItem: ImageItem | null = null;
    const isModalOpen = writable(false);
    let selectedImageForModal: ImageItem | null = null;
    let parentForm: HTMLFormElement | null = null;

    let cropEditSrc: string | null = null;
    let cropEditItem: ImageItem | null = null;
    let cropEditCroppedFile: File | null = null;
    let cropEditCroppedBlobUrl: string = '';
    let isCropEditUploading = false;

    onMount(() => {
        const hiddenInput = document.querySelector(
            `input[name="${inputName}"]`,
        );
        if (hiddenInput) {
            let element = hiddenInput.parentElement;
            while (element && element.tagName !== 'FORM') {
                element = element.parentElement;
            }
            parentForm = element as HTMLFormElement;
        }
    });

    onDestroy(() => {
        if (typeof window !== 'undefined') {
            window.removeEventListener('keydown', handleKeyDown);
        }
        blobUrlsToRevoke.forEach(URL.revokeObjectURL);
    });

    $: hiddenInputValue = JSON.stringify(
        imageItems
            .filter((item) => item.status === 'uploaded' && item.path)
            .map((item) => ({
                id: item.id,
                url: item.path,
                imageWidth: item.width ?? null,
                imageHeight: item.height ?? null,
                position: item.position,
                focalPoint: item.focalPoint
                    ? `${item.focalPoint.x}% ${item.focalPoint.y}%`
                    : '50% 50%',
            })),
    );

    const handleImageUploading = (
        event: CustomEvent<{ tempId: string; file: File }>,
    ) => {
        const { tempId, file } = event.detail;

        if (maxImages !== null && imageItems.length >= maxImages) {
            console.warn(
                'Maximum image limit reached. Ignoring file:',
                file.name,
            );
            return;
        }

        const blobUrl = URL.createObjectURL(file);
        blobUrlsToRevoke.push(blobUrl);

        const newItem: ImageItem = {
            tempId,
            file,
            blobUrl,
            status: 'uploading',
            position: imageItems.length,
        };
        imageItems = [...imageItems, newItem];
    };

    const handleImageUploaded = (
        event: CustomEvent<{
            tempId: string;
            path: string;
            width: number;
            height: number;
            focalPoint?: { x: number; y: number };
        }>,
    ) => {
        const { tempId, path, width, height, focalPoint } = event.detail;

        imageItems = imageItems.map((item) => {
            if (item.tempId === tempId) {
                if (item.blobUrl) {
                    URL.revokeObjectURL(item.blobUrl);
                    blobUrlsToRevoke = blobUrlsToRevoke.filter(
                        (url) => url !== item.blobUrl,
                    );
                }
                return {
                    ...item,
                    status: 'uploaded',
                    path: path,
                    width,
                    height,
                    focalPoint: focalPoint ?? { x: 50, y: 50 },
                    error: undefined,
                    file: undefined,
                    blobUrl: undefined,
                };
            }
            return item;
        });
    };

    const handleImageError = (
        event: CustomEvent<{ tempId: string; error: string }>,
    ) => {
        const { tempId, error } = event.detail;
        console.error(
            'MultiMediaSelector: Image upload failed for tempId',
            tempId,
            error,
        );
        Sentry.captureException(new Error(error), {
            extra: {
                component: 'MultiMediaSelector',
                context: 'ImageUploadErrorReceived',
                tempIdReceived: tempId,
                errorMessage: error,
            },
        });

        const itemToRemove = imageItems.find((item) => item.tempId === tempId);
        if (itemToRemove?.blobUrl) {
            URL.revokeObjectURL(itemToRemove.blobUrl);
            blobUrlsToRevoke = blobUrlsToRevoke.filter(
                (url) => url !== itemToRemove.blobUrl,
            );
        }
        imageItems = imageItems.filter((item) => item.tempId !== tempId);
    };

    const removeImage = (tempIdToRemove: string) => {
        const itemToRemove = imageItems.find(
            (item) => item.tempId === tempIdToRemove,
        );

        if (itemToRemove?.blobUrl) {
            URL.revokeObjectURL(itemToRemove.blobUrl);
            blobUrlsToRevoke = blobUrlsToRevoke.filter(
                (url) => url !== itemToRemove.blobUrl,
            );
        }

        imageItems = imageItems
            .filter((item) => item.tempId !== tempIdToRemove)
            .map((item, index) => ({
                ...item,
                position: index,
            }));
    };

    // --- Modal Handlers ---
    const openModal = (item: ImageItem): void => {
        if (item.status === 'uploaded' && item.path) {
            selectedImageForModal = item;
            $isModalOpen = true;
        }
    };

    const closeModal = (): void => {
        $isModalOpen = false;
        selectedImageForModal = null;
    };

    const navigateToPrevious = (): void => {
        if (!selectedImageForModal) return;

        const uploadedItems = imageItems.filter(
            (item) => item.status === 'uploaded',
        );
        const currentIndex = uploadedItems.findIndex(
            (item) => item.tempId === selectedImageForModal?.tempId,
        );

        if (currentIndex > 0) {
            selectedImageForModal = uploadedItems[currentIndex - 1];
        } else {
            selectedImageForModal = uploadedItems[uploadedItems.length - 1];
        }
    };

    const navigateToNext = (): void => {
        if (!selectedImageForModal) return;

        const uploadedItems = imageItems.filter(
            (item) => item.status === 'uploaded',
        );
        const currentIndex = uploadedItems.findIndex(
            (item) => item.tempId === selectedImageForModal?.tempId,
        );

        if (currentIndex < uploadedItems.length - 1) {
            selectedImageForModal = uploadedItems[currentIndex + 1];
        } else {
            selectedImageForModal = uploadedItems[0];
        }
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
        if (!$isModalOpen) return;

        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                navigateToPrevious();
                break;
            case 'ArrowRight':
                event.preventDefault();
                navigateToNext();
                break;
            case 'Escape':
                event.preventDefault();
                closeModal();
                break;
        }
    };

    $: if ($isModalOpen && typeof window !== 'undefined') {
        window.addEventListener('keydown', handleKeyDown);
    } else if (typeof window !== 'undefined') {
        window.removeEventListener('keydown', handleKeyDown);
    }

    // --- Computed ---
    $: currentImageCount = imageItems.length;
    $: successfulImageCount = imageItems.filter(
        (item) => item.status === 'uploaded',
    ).length;
    $: canUploadMore = maxImages == null || currentImageCount < maxImages;
    $: meetsMinimum = successfulImageCount >= minImages;
    $: uploadedItems = imageItems.filter((item) => item.status === 'uploaded');
    $: showNavigationArrows = uploadedItems.length > 1;

    // Handle form submission state based on upload status
    $: {
        const uploadingImageCount = imageItems.filter(
            (item) => item.status === 'uploading',
        ).length;

        const disableFormSubmission = () => {
            if (parentForm) {
                const formId = parentForm.id;

                // Find submit buttons inside the form
                const submitButtonsInForm = parentForm.querySelectorAll(
                    'button[type="submit"], input[type="submit"]',
                );

                // Find submit buttons outside the form that reference this form via form attribute
                const submitButtonsOutsideForm = formId
                    ? document.querySelectorAll(
                          `button[type="submit"][form="${formId}"], input[type="submit"][form="${formId}"]`,
                      )
                    : [];

                const allSubmitButtons = [
                    ...submitButtonsInForm,
                    ...submitButtonsOutsideForm,
                ];

                allSubmitButtons.forEach((button) => {
                    const btn = button as HTMLButtonElement | HTMLInputElement;
                    // Only store original state if not already stored (first time disabling)
                    if (!btn.dataset.originalDisabled) {
                        btn.dataset.originalDisabled = btn.disabled
                            ? 'true'
                            : 'false';
                    }
                    btn.disabled = true;
                });
            }
        };

        const enableFormSubmission = () => {
            if (parentForm) {
                const formId = parentForm.id;

                // Find submit buttons inside the form
                const submitButtonsInForm = parentForm.querySelectorAll(
                    'button[type="submit"], input[type="submit"]',
                );

                // Find submit buttons outside the form that reference this form via form attribute
                const submitButtonsOutsideForm = formId
                    ? document.querySelectorAll(
                          `button[type="submit"][form="${formId}"], input[type="submit"][form="${formId}"]`,
                      )
                    : [];

                const allSubmitButtons = [
                    ...submitButtonsInForm,
                    ...submitButtonsOutsideForm,
                ];

                allSubmitButtons.forEach((button) => {
                    const btn = button as HTMLButtonElement | HTMLInputElement;
                    btn.disabled = btn.dataset.originalDisabled === 'true';
                    // Clear the stored original state so it can be properly stored next time
                    delete btn.dataset.originalDisabled;
                });
            }
        };

        if (uploadingImageCount > 0 || isCropEditUploading) {
            disableFormSubmission();
        } else {
            enableFormSubmission();
        }
    }

    // --- Helper Text Logic ---
    $: helperText = (() => {
        const current = successfulImageCount;
        const parts = [];

        // Current count / Max limit part
        if (maxImages !== null) {
            parts.push(
                `${current} / ${maxImages} image${maxImages !== 1 ? 's' : ''}`,
            );
        }

        // Min requirement part (only if required, > 0, and not met)
        if (required && minImages > 0 && !meetsMinimum) {
            parts.push(
                `Le nombre minimum de ${minImages} photo${
                    minImages > 1 ? 's' : ''
                } n'est pas atteint`,
            );
        }

        return parts.join(' ');
    })();

    $: helperTextColorClass =
        required && minImages > 0 && !meetsMinimum
            ? 'text-status-negative'
            : '';

    // --- Image List Handlers ---
    const handleImageListReorder = (
        event: CustomEvent<{ items: ImageItem[] }>,
    ) => {
        imageItems = event.detail.items;
    };

    const handleImagePreview = (event: CustomEvent<{ item: ImageItem }>) => {
        openModal(event.detail.item);
    };

    const handleEditFocalPoint = (event: CustomEvent<{ tempId: string }>) => {
        const item = imageItems.find((i) => i.tempId === event.detail.tempId);
        if (item) {
            focalPointEditorItem = item;
        }
    };

    const handleEditCrop = (event: CustomEvent<{ tempId: string }>) => {
        const item = imageItems.find((i) => i.tempId === event.detail.tempId);
        if (!item?.path) return;
        cropEditItem = item;
        cropEditSrc = item.path;
    };

    const handleCropEditCropped = (event: CustomEvent<{ file: File }>) => {
        cropEditCroppedFile = event.detail.file;
        cropEditCroppedBlobUrl = URL.createObjectURL(cropEditCroppedFile);
    };

    const handleCropEditFocalPointConfirmed = async (
        event: CustomEvent<{ focalPoint: { x: number; y: number } }>,
    ) => {
        const { focalPoint } = event.detail;
        const file = cropEditCroppedFile!;
        const editingTempId = cropEditItem!.tempId;

        URL.revokeObjectURL(cropEditCroppedBlobUrl);
        cropEditCroppedBlobUrl = '';
        cropEditCroppedFile = null;
        cropEditItem = null;
        cropEditSrc = null;
        isCropEditUploading = true;

        try {
            const uploadedImage = await uploadImageToMedia(
                apiEndpoint,
                mediaBaseUrl,
                file,
            );
            imageItems = imageItems.map((item) =>
                item.tempId === editingTempId
                    ? {
                          ...item,
                          path: uploadedImage.url,
                          width: uploadedImage.width,
                          height: uploadedImage.height,
                          focalPoint,
                      }
                    : item,
            );
        } catch (error) {
            console.error('Failed to re-upload cropped image:', error);
            Sentry.captureException(error);
        } finally {
            isCropEditUploading = false;
        }
    };

    const handleCropEditFocalPointCancelled = () => {
        URL.revokeObjectURL(cropEditCroppedBlobUrl);
        cropEditCroppedBlobUrl = '';
        cropEditCroppedFile = null;
        // cropEditFile still set → ImageCropper re-appears
    };

    const handleCropEditCancelled = () => {
        cropEditSrc = null;
        cropEditItem = null;
    };

    const handleFocalPointConfirm = (
        event: CustomEvent<{ focalPoint: { x: number; y: number } }>,
    ) => {
        const tempId = focalPointEditorItem!.tempId;
        imageItems = imageItems.map((item) =>
            item.tempId === tempId
                ? { ...item, focalPoint: event.detail.focalPoint }
                : item,
        );
        focalPointEditorItem = null;
    };

    // Add these functions to handle validation events
    let showHelper = false;
    const handleInvalid = (event: Event) => {
        showHelper = true;
        const input = event.target as HTMLInputElement;
        const minImages = input.getAttribute('data-min-images');
        input.setCustomValidity(
            `Veuillez télécharger au moins ${minImages} image${
                Number(minImages) > 1 ? 's' : ''
            }`,
        );
    };
</script>

<div class="flex flex-col gap-m pt-m">
    <!-- Helper text displaying image counts and requirements -->
    {#if showHelper && helperText && ((required && minImages > 0) || maxImages !== null)}
        <div class="flex items-center gap-2xs">
            <VpWarningErrorIcon class="size-m text-status-negative" />
            <p class={cn('text-sm', helperTextColorClass)}>
                {helperText}
            </p>
        </div>
    {/if}
    {#if canUploadMore}
        <MultiMediaUpload
            {apiEndpoint}
            {mediaBaseUrl}
            on:uploading={handleImageUploading}
            on:uploaded={handleImageUploaded}
            on:error={handleImageError}
            {cropProxyUrl}
        />
    {:else if maxImages !== null}
        <p class="text-sm">
            {trans('form.multi_image_upload.max_images', {
                '%maxImages%': String(maxImages),
            })}
        </p>
    {/if}

    {#if imageItems.length > 0}
        <!-- Infobox: reorder info -->
        {#if imageItems.length > 1}
            <div
                class="flex items-center gap-m border border-solid border-border-brand bg-commercial-neutral px-m py-xs text-content-brand"
            >
                <VpLightbulbIcon class="size-xl text-content-brand" />
                <p>
                    {trans('form.multi_image_upload.rearrange')}
                </p>
            </div>
        {/if}
        <MultiMediaList
            items={imageItems}
            {showCoverLabel}
            on:reorder={handleImageListReorder}
            on:remove={({ detail }) => removeImage(detail.tempId)}
            on:preview={handleImagePreview}
            on:editFocalPoint={handleEditFocalPoint}
            on:editCrop={handleEditCrop}
        />
    {/if}

    <!-- Hidden input storing the JSON array of successful uploads -->
    <input type="hidden" name={inputName} value={hiddenInputValue} />

    <!-- Visually hidden input for validation -->
    {#if required && minImages > 0 && successfulImageCount < minImages}
        <input
            class="sr-only"
            tabindex="-1"
            aria-hidden="true"
            type="text"
            required
            value=""
            title={`Minimum ${minImages} image${
                minImages > 1 ? 's' : ''
            } required`}
            data-min-images={minImages}
            on:invalid={handleInvalid}
        />
    {/if}
</div>

<!-- Modal for Image Preview -->
<Modal isOpen={isModalOpen} onClose={closeModal}>
    <div slot="header" class="flex items-center justify-between">
        <h2 class="text-lg font-medium">
            {trans('form.multi_image_upload.image_preview')}
        </h2>
        <button
            on:click={closeModal}
            aria-label="Close modal"
            class="focus:ring-border-focus rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
        >
            <VpCloseIcon class="size-xl text-content-neutral" />
        </button>
    </div>
    {#if selectedImageForModal && selectedImageForModal.status === 'uploaded'}
        {@const modalImageUrl = selectedImageForModal.path}
        {#if modalImageUrl}
            <div
                class="relative flex h-[calc(100vh-110px)] items-center justify-center p-xl"
            >
                <!-- Previous Arrow -->
                {#if showNavigationArrows}
                    <button
                        type="button"
                        on:click={navigateToPrevious}
                        aria-label="Image précédente"
                        class="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full p-2 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2"
                    >
                        <VpArrowLeftIcon class="size-xl" />
                    </button>
                {/if}

                <!-- Image -->
                <img
                    src={modalImageUrl}
                    alt="Full size preview"
                    class="max-h-full max-w-full object-contain"
                />

                <!-- Next Arrow -->
                {#if showNavigationArrows}
                    <button
                        type="button"
                        on:click={navigateToNext}
                        aria-label="Image suivante"
                        class="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full p-2 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2"
                    >
                        <VpArrowRightIcon class="size-xl" />
                    </button>
                {/if}
            </div>
        {/if}
    {/if}
</Modal>

<!-- Post-crop focal point editor -->
{#if cropEditCroppedFile && cropEditCroppedBlobUrl}
    <FocalPointEditor
        src={cropEditCroppedBlobUrl}
        initialFocalPoint={{ x: 50, y: 50 }}
        on:confirm={handleCropEditFocalPointConfirmed}
        on:cancel={handleCropEditFocalPointCancelled}
    />
{:else if cropEditSrc}
    <!-- Crop Edit Modal -->
    {#key cropEditSrc}
        <ImageCropEditor
            src={cropEditSrc}
            {cropProxyUrl}
            on:cropped={handleCropEditCropped}
            on:cancel={handleCropEditCancelled}
        />
    {/key}
{:else if focalPointEditorItem?.path}
    <!-- Standalone focal point editor for existing images -->
    <FocalPointEditor
        src={imageOptimize(focalPointEditorItem.path, '1920x0', 'avif')}
        initialFocalPoint={focalPointEditorItem.focalPoint ?? { x: 50, y: 50 }}
        on:confirm={handleFocalPointConfirm}
        on:cancel={() => {
            focalPointEditorItem = null;
        }}
    />
{/if}
